/**
 * BYOK key-manager API client.
 *
 * Thin typed functions over the shared `apiClient`. No caching, no state — the
 * plaintext key is passed straight through to `PUT` and is unreachable
 * afterwards.
 *
 * @module ai_services/api/ai-keys-client
 */

import { apiClient } from "@shared/lib/api/client";
import type {
  ByokKey,
  ByokKeyListResponse,
  ByokProvider,
  ByokStatus,
  ByokUsageRange,
  ByokUsageSummary,
  AIModelListResponse,
  ModelPreferencesResponse,
  SaveByokKeyRequest,
  SaveModelPreferencesRequest,
} from "./types";

const AI_KEYS_BASE = "/api/v1/ai-keys";

/** Every provider, configured or not. */
export async function listByokKeys(): Promise<ByokKeyListResponse> {
  return apiClient.get<ByokKeyListResponse>(`${AI_KEYS_BASE}/`);
}

/** Deployment flags, the caller's keys, and per-feature availability. */
export async function getByokStatus(): Promise<ByokStatus> {
  return apiClient.get<ByokStatus>(`${AI_KEYS_BASE}/status/`);
}

/** The caller's own AI spend rollup. Defaults server-side to the last 30 days. */
export async function getByokUsage(
  range: ByokUsageRange = {},
): Promise<ByokUsageSummary> {
  const params = new URLSearchParams();
  if (range.since) params.set("since", range.since);
  if (range.until) params.set("until", range.until);
  const query = params.toString();
  return apiClient.get<ByokUsageSummary>(
    `${AI_KEYS_BASE}/usage/${query ? `?${query}` : ""}`,
  );
}

/**
 * Store or replace one provider key.
 *
 * The server validates against the provider before persisting, so a resolved
 * promise means the key actually works. A rejected one leaves any previously
 * stored key untouched.
 */
export async function saveByokKey({
  provider,
  ...body
}: SaveByokKeyRequest): Promise<ByokKey> {
  return apiClient.put<ByokKey>(`${AI_KEYS_BASE}/${provider}/`, body);
}

/** Re-validate an already stored key. */
export async function testByokKey(provider: ByokProvider): Promise<ByokKey> {
  return apiClient.post<ByokKey>(`${AI_KEYS_BASE}/${provider}/test/`);
}

/** Revoke a stored key. Idempotent — revoking an absent key also succeeds. */
export async function deleteByokKey(provider: ByokProvider): Promise<void> {
  await apiClient.delete<void>(`${AI_KEYS_BASE}/${provider}/`);
}

/** Selectable, priced models for a provider, optionally narrowed by feature. */
export async function listModels(
  provider: ByokProvider,
  feature?: string,
): Promise<AIModelListResponse> {
  const params = new URLSearchParams({ provider });
  if (feature) params.set("feature", feature);
  return apiClient.get<AIModelListResponse>(
    `${AI_KEYS_BASE}/models/?${params.toString()}`,
  );
}

/** Explicit preferences plus deploy-time defaults for every configured feature. */
export async function getModelPreferences(): Promise<ModelPreferencesResponse> {
  return apiClient.get<ModelPreferencesResponse>(
    `${AI_KEYS_BASE}/model-preferences/`,
  );
}

/** Partially update preferences; null entries restore their system defaults. */
export async function saveModelPreferences(
  request: SaveModelPreferencesRequest,
): Promise<ModelPreferencesResponse> {
  return apiClient.put<ModelPreferencesResponse>(
    `${AI_KEYS_BASE}/model-preferences/`,
    request,
  );
}
